import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import {
  CONFIG_BASE_DATE_RANGEPICKER,
  SORT_DIRECTION,
} from '../../../../../../../core/constants/constants';
import { ChargesWithdrawalsService } from '../../../../../../../core/services/charges-withdrawals.service';
import { FilterCommunicationService } from '../../../../../../../core/services/filter-communication.service';
import { StateService } from '../../../../../../../core/services/state.service';
import { selectAuth } from '../../../../../../../core/state/auth/auth.selector';
import { formatAmount } from '../../../../../../../core/utils/format-numeric.utils';
import { HttpErrorResponse } from '@angular/common/http';
import { extractErrorMessage } from '../../../../../../../core/utils/extract-error-message.util';
import {
  MOVEMENT_CLASSES,
  MOVEMENT_TYPE,
  STATE_LW_CLASSES,
  columnsTableLw,
} from '../../../../../../../shared/data/loads-withdrawal/constants';
import { ActivatedRoute, Router } from '@angular/router';
import { selectScreen } from '../../../../../../../core/state/screen/screen.selectors';
import { UserService } from '../../../../../../../core/services/user.service';
import { Subscription } from 'rxjs';
import { LOADS_WITHDRAWAL_BUTTON_PERMISSIONS } from '../../../../../../../shared/data/config/constants-buttons';
import {
  Permission,
  MainPermission,
  Profile,
} from '../../../../../../../core/interfaces/permision.interface';
import { AuditActionModalComponent } from '../../../../../../../shared/components/modals/customize-modals/audit-action-modal/audit-action-modal.component';
import {
  ChargesWithdrawalsFilters,
  ChargesWithdrawalsListItem,
} from '../../../../../../../core/interfaces/charges-withdrawals.interface';
import { SubscriptionPaymentState } from '../../../../../../../core/interfaces/subscription-payment.interface';
import { IProfileState } from '../../../../../../../core/interfaces/user.interface';

type ChargesWithdrawalsTableItem = Omit<ChargesWithdrawalsListItem, 'monto'> & {
  monto: string;
  updated_by: string | null;
};

type ListLwColumn = (typeof columnsTableLw)[number];
const LW_CANCELLED_STATE = 9;

@Component({
  selector: 'app-list-lw-table',
  templateUrl: './list-lw-table.component.html',
  styleUrl: './list-lw-table.component.scss',
})
export class ListLwTableComponent implements OnInit, OnDestroy {
  // LOADER
  public isLoading: boolean = false;

  // TABLE
  public lwData: ChargesWithdrawalsTableItem[] = [];
  public columnsTable: ListLwColumn[] = [];
  public totalDolares: string = '';
  public totalSoles: string = '';
  public states: SubscriptionPaymentState[] = [];
  public selectedComment: string = '';
  public SORT_DIRECTION = SORT_DIRECTION;
  public LOADS_WITHDRAWAL_BUTTON_PERMISSIONS =
    LOADS_WITHDRAWAL_BUTTON_PERMISSIONS;

  // PAGINATION
  public collectionSize: number = 0;
  public page: number = 1;
  public limit: number = 10;
  public sortColumn: string | null = null;
  public sortDirection: string | null = null;

  //FILTER
  public selectedFilter: ChargesWithdrawalsFilters | null = null;
  public defaultFilter: ChargesWithdrawalsFilters = {};
  public startDate = CONFIG_BASE_DATE_RANGEPICKER.minDate;
  public endDate = CONFIG_BASE_DATE_RANGEPICKER.initialRange[1];
  public isMobile: boolean = false;
  public token!: string;
  public subscribe: Subscription = new Subscription();

  //LOGGEDSUSER
  public profile$ = this.store.select(selectAuth);
  public profile: (IProfileState & Partial<Profile>) | null = null;

  // REDUX
  public isMobile$ = this.store.select(selectScreen);
  public isMobileRedux!: boolean;

  constructor(
    private _filterCommunicationService: FilterCommunicationService,
    private stateService: StateService,
    private store: Store,
    private lwService: ChargesWithdrawalsService,
    private modalService: NgbModal,
    private router: Router,
    private activedRouter: ActivatedRoute,
    private _userService: UserService
  ) {}

  async ngOnInit(): Promise<void> {
    const fullUrl = this.router.url;
    this.isMobile = fullUrl.includes('/mobile');
    this.isMobile$.subscribe((isMobile) => {
      this.isMobileRedux = isMobile;
    });
    this.columnsTable = this.isMobile
      ? columnsTableLw.filter((column) => column.name !== 'Acciones')
      : columnsTableLw;
    if (this.isMobile) {
      await this.loadTokenLogged();
    } else {
      await this.loadProfileLogged();
    }
    this.getDataFromFilter();
    this.getStatesLw();
  }

  //? LOAD DATA
  async loadProfileLogged() {
    await new Promise<void>((resolve, reject) => {
      this.profile$.subscribe({
        next: (res: IProfileState) => {
          if (res.idusuario !== -1) {
            this.profile = res;
            resolve();
          }
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }
  async loadTokenLogged() {
    await new Promise<void>((resolve, reject) => {
      this.activedRouter.params.subscribe((params) => {
        this.token = params['token'];
        this._userService.getUserByTokenApp(this.token).subscribe({
          next: (res) => {
            if (res.data.idusuario) {
              this.profile = res.data as IProfileState & Partial<Profile>;
              resolve();
            } else {
              reject(new Error('No user id found'));
            }
          },
          error: (err) => {
            reject(err);
          },
        });
      });
    });
  }
  getData(): void {
    this.isLoading = true;

    this.defaultFilter = this.selectedFilter
      ? this.selectedFilter
      : {
          date_star: moment(this.startDate).format('YYYY-MM-DD HH:mm:ss'),
          date_end: moment(this.endDate).format('YYYY-MM-DD 23:59:59'),
        };
    const filterWithSorting = {
      ...this.defaultFilter,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
    };

    this.lwService
      .getAllLw(this.page, this.limit, filterWithSorting)
      .subscribe((res) => {
        const response = res.data;
        this.isLoading = false;
        this.totalDolares = response.totalMontoSummary.Ecuador
          ? formatAmount(Number(response.totalMontoSummary.Ecuador), 'EC')
          : '$ 0';
        this.totalSoles = response.totalMontoSummary.Peru
          ? formatAmount(Number(response.totalMontoSummary.Peru), 'PE')
          : 'S/ 0';
        this.lwData = response.data.map((item) => ({
          ...this.formatLwData(item),
        }));
        this.collectionSize = response.totalCount;
      });
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection =
        this.sortDirection === SORT_DIRECTION.ASC
          ? SORT_DIRECTION.DESC
          : SORT_DIRECTION.ASC;
    } else {
      this.sortColumn = column;
      this.sortDirection = SORT_DIRECTION.ASC;
    }
    this.getData();
  }

  getStatesLw() {
    this.stateService.geatAllDataLw().subscribe((response) => {
      this.states = Array.isArray(response.data) ? response.data : [];
    });
  }
  formatLwData(item: ChargesWithdrawalsListItem): ChargesWithdrawalsTableItem {
    const monto = formatAmount(
      Number(item.monto),
      item.id_pais === 1 ? 'EC' : 'PE'
    );
    const updatedBy =
      typeof item.updated_by === 'string'
        ? item.updated_by
        : item.updated_by?.usuario || item.updated_by?.nombres || null;

    return {
      ...item,
      monto: monto,
      updated_by: updatedBy,
    };
  }
  hasComment(comment: string | null | undefined): boolean {
    return this.normalizeComment(comment).length > 0;
  }

  normalizeComment(comment: string | null | undefined): string {
    return typeof comment === 'string' ? comment.trim() : '';
  }

  openCommentModal(
    content: TemplateRef<unknown>,
    comment: string | null | undefined,
  ) {
    const normalizedComment = this.normalizeComment(comment);
    if (!normalizedComment) return;

    this.selectedComment = normalizedComment;
    this.modalService.open(content, {
      centered: true,
      size: 'lg',
    });
  }

  //?FILTER
  getDataFromFilter() {
    this.subscribe = this._filterCommunicationService.currentFilter.subscribe(
      (filter: ChargesWithdrawalsFilters | null) => {
        if (this.isMobile) {
          if (filter) {
            this.selectedFilter = filter;
            this.getData();
          }
        } else {
          this.selectedFilter = filter;
          this.getData();
        }
      }
    );
  }

  ngOnDestroy(): void {
    this.subscribe.unsubscribe();
  }

  //*BUTTONS
  reloadTable() {
    this.isLoading = true;
    this.sortColumn = null;
    this.sortDirection = null;
    this.getData();
  }
  setStatus(item: ChargesWithdrawalsTableItem, idestado: number) {
    if (!item.idcargas_retiros) return;

    if (idestado !== LW_CANCELLED_STATE) {
      this.executeStatusChange(item.idcargas_retiros, idestado);
      return;
    }

    const nextStateName = this.getStateName(idestado);
    const modalRef = this.modalService.open(AuditActionModalComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = `Cambiar estado a ${nextStateName}`;
    modalRef.componentInstance.placeholder =
      'Ingrese una observación para el cambio de estado';
    modalRef.componentInstance.required = true;

    modalRef.result
      .then((comment: string) => {
        const observation = this.normalizeComment(comment);
        if (!observation) return;

        this.executeStatusChange(item.idcargas_retiros, idestado, observation);
      })
      .catch(() => {});
  }

  executeStatusChange(
    idcargas_retiros: number,
    idestado: number,
    comentario: string = ''
  ) {
    this.lwService.setState(idcargas_retiros, idestado, comentario).subscribe({
      next: () => {
        Swal.fire({
          title: 'Estado actualizado 👌!',
          text: 'El estado fue actualizado correctamente.',
          icon: 'success',
        });
        this.reloadTable();
      },
      error: (error: HttpErrorResponse) => {
        Swal.fire({
          title: 'No se pudo actualizar',
          text: extractErrorMessage(error, 'es', 'Ocurrió un problema al cambiar el estado.'),
          icon: 'error',
        });
      },
    });
  }

  exportToExcel() {
    const fileName = 'cargas_retiros_data.xlsx';
    const worksheetName = 'Cargas Y Retiros Data';
    const excelData = this.lwData.map((item) => {
      const montoOriginal = item.monto.trim();
      const montoConFormato = montoOriginal
        .replace(/[$S/]/g, '')
        .replace(/−/g, '-')
        .replace(/\s/g, '');

      const montoSinMoneda = parseFloat(montoConFormato.replace(',', '.'));
      const moneda = item.monto.includes('$')
        ? 'USD'
        : item.monto.includes('S/')
        ? 'PEN'
        : 'Desconocido';

      return {
        Estado:
          STATE_LW_CLASSES[Number(item.id_estado ?? -1)]?.text || 'Desconocido',
        Creado: item.fecha,
        Monto: montoSinMoneda,
        Moneda: moneda,
        categoria:
          MOVEMENT_TYPE.find((type) => type.value === String(item.id_categoria))
            ?.label || 'Desconocido',
        Titular: item.titular?.usuario,
        Agente: item.agente?.usuario,
        'Actualizado Por': item.updated_by || '-',
        Comentario: item.comentario,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, worksheetName);
    XLSX.writeFile(workbook, fileName);
  }

  //*PAGINATION
  // Métodos para manejar el cambio de página y el cambio de límite
  onPageChange(page: number): void {
    this.page = page;
    this.getData();
  }

  onLimitChange(limit: number): void {
    this.limit = limit;
    this.getData();
  }

  //? STATE CLASSES
  getStateClasses(state: number) {
    return STATE_LW_CLASSES[state] || { text: '', classes: {} };
  }

  getMovementClass(type: number): string {
    return MOVEMENT_CLASSES[type] || '';
  }

  getMovementIconClass(type: number): string {
    return this.getMovementIconClassFromTypes(type) || '';
  }

  getMovementIconClassFromTypes(type: number): string {
    // Aquí puedes definir los íconos específicos para cada tipo de movimiento, similar a TRANSACTION_ICON_CLASSES
    const MOVEMENT_ICON_CLASSES: { [key: number]: string } = {
      1: 'fas fa-credit-card', // Ejemplo de ícono para 'Credito'
      2: 'fas fa-money-bill', // Ejemplo de ícono para 'Debito'
    };
    return MOVEMENT_ICON_CLASSES[type] || '';
  }

  getMovementTypeLabel(type: number): string {
    const typeObj = MOVEMENT_TYPE.find((t) => Number(t.value) === type);
    return typeObj ? typeObj.label : 'Desconocido';
  }

  getStateName(stateId: number): string {
    const state = this.states?.find(
      (item) => Number(item.n_estado) === Number(stateId)
    );

    if (state?.estado) {
      return state.estado;
    }

    return (
      STATE_LW_CLASSES[stateId]?.text?.replace(/<br>/g, ' ') || 'seleccionado'
    );
  }

  hasPermission(permissionId: number): boolean {
    return (
      this.profile?.rol?.permisos_principales.some(
      (mainPermission: MainPermission) =>
        mainPermission.permisos.some(
          (permission: Permission) => permission.id === permissionId
        )
      ) ?? false
    );
  }
}
